// src/pages/TrainingDetails.tsx
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { supabase } from "../supabaseClient";
import ShootingBlock from "../components/ShootingBlock";
import { Capacitor } from "@capacitor/core";
import SendReminderButton from "../components/SendReminderButton";
import { LogoLoader } from "../components/LogoLoader";
import { TrainingDrawingCanvas } from "../components/TrainingDrawingCanvas";
import ExportTrainingPlanPdfButton from "../components/ExportTrainingPlanPdfButton";
import {
  CalendarDays,
  Users,
  NotebookPen,
  ArrowLeft,
  Pencil,
  X,
  Trash2,
  PlusCircle,
  Clock,
  Save,
} from "lucide-react";
import html2canvas from "html2canvas";
import i18n from "../i18n";

// ⬇️ viewer de la séance de tir (lecture seule)
import ShootingBlockViewer from "../components/ShootingBlockViewer";

/* ========= i18n mini-hook ========= */
function useI18nSafe() {
  const [lng, setLng] = useState((i18n.resolvedLanguage || i18n.language || "fr").toLowerCase());
  useEffect(() => {
    const onChange = (l: string) => setLng(l.toLowerCase());
    i18n.on("languageChanged", onChange);
    return () => i18n.off("languageChanged", onChange);
  }, []);
  const t = (key: string, options?: Record<string, unknown>): string =>
    options != null ? String((i18n.t as any)(key, options)) : String((i18n.t as any)(key));
  return { t, lng };
}

// ---- Types PlanTraining-compat ----
type BlockKind = "echauffement" | "prepa" | "exercice" | "shooting";
type CourtType = "half" | "full";

type DrawElementKind = "arrow" | "cone" | "player" | "text" | "pass";
type DrawElement = {
  id: string;
  kind: DrawElementKind;
  x: number;
  y: number; // normalisés 0..1
  x2?: number;
  y2?: number; // pour arrow/pass
  label?: string; // pour texte / numéro
  color?: string;
  cpx?: number;
  cpy?: number; // contrôle quadratique pour arrow
};

type DrawingV2 = {
  id: string;
  paths: { x: number; y: number }[][];
  type: CourtType;
  elements?: DrawElement[];
};

type ExerciseBlock = {
  id: string;
  title?: string;
  duration: string;
  objectives: string[];
  description: string;
  technicalNotes: string;
  evolutions: string;
  drawings: DrawingV2[];
  // champs possibles
  name?: string;
  kind?: BlockKind;
};

type Training = {
  id: string;
  date: string;
  team_id?: string | null;
  notes?: string | null;
};

type TeamPlayer = {
  id?: string;
  firstName?: string;
  lastName?: string;
  name?: string;
  number?: number;
  position?: string;
  player_number?: number;
};

type TeamRow = {
  id: string;
  team_name: string;
  players: TeamPlayer[] | null;
};

// ---- helpers date ↔️ états ----
const splitIsoToLocalDateTime = (iso?: string): [string, string] => {
  if (!iso) return ["", ""];
  const hasTime = /T\d{2}:\d{2}/.test(iso);
  if (!hasTime) return [iso.slice(0, 10), ""];
  const d = new Date(iso);
  const date = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const time = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  return [date, time];
};



function sanitizePaths(paths?: { x: number; y: number }[][]): { x: number; y: number }[][] {
  if (!Array.isArray(paths)) return [];
  return paths
    .map((seg) =>
      Array.isArray(seg)
        ? seg.filter(
          (pt) => pt && Number.isFinite((pt as any).x) && Number.isFinite((pt as any).y)
        )
        : []
    )
    .filter((seg) => seg.length > 0);
}

// --- hook: mesure la largeur d’un conteneur en temps réel
function useContainerWidth<T extends HTMLElement = HTMLDivElement>() {
  const ref = useRef<T | null>(null);
  const [w, setW] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new ResizeObserver((entries) => {
      for (const e of entries) {
        if (e.contentRect?.width != null) {
          setW(e.contentRect.width);
        }
      }
    });
    obs.observe(el);
    // init immédiate
    setW(el.getBoundingClientRect().width);
    return () => obs.disconnect();
  }, []);

  return { ref, width: w };
}

const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);
const combineLocalToStored = (dateStr: string, timeStr: string): string => {
  if (!dateStr) return "";
  if (!timeStr) return dateStr;
  const [y, m, d] = dateStr.split("-").map(Number);
  const [hh, mm] = timeStr.split(":").map(Number);
  return `${y}-${pad(m)}-${pad(d)}T${pad(hh)}:${pad(mm)}:00`;
};

// ---------- Overlay read-only (SVG) qui scale avec la largeur ----------
const ElementsOverlayRO: React.FC<{
  elements: DrawElement[];
  width: number;
  height: number;
  iconScale?: number;
}> = ({ elements, width, height, iconScale = 1 }) => {
  const basePlayerR = 16;
  const r = basePlayerR * iconScale;
  const baseFont = 14;
  const font = Math.max(10, Math.round(baseFont * iconScale));

  return (
    <svg className="pointer-events-none absolute inset-0" width={width} height={height}>
      {elements.map((el) => {
        const x = el.x * width;
        const y = el.y * height;

        if (el.kind === "cone") {
          const size = 18 * iconScale;
          return (
            <polygon
              key={el.id}
              points={`${x},${y - size} ${x - size * 0.6},${y + size * 0.6} ${x + size * 0.6},${y + size * 0.6}`}
              fill={el.color || "#ef4444"}
              opacity={0.95}
            />
          );
        }

        if (el.kind === "player") {
          const hasBall = (el as any).hasBall; // 🔥 le flag existe dans les données

          return (
            <g key={el.id}>
              {/* Cercle ballon si hasBall = true */}
              {hasBall && (
                <circle
                  cx={x}
                  cy={y}
                  r={r + 6}
                  fill="none"
                  stroke={el.color || "#facc15"}   // même couleur que le joueur
                  strokeWidth={3}
                  opacity={0.9}
                />
              )}

              {/* Pion joueur */}
              <circle cx={x} cy={y} r={r} fill={el.color || "#2563eb"} opacity={0.98} />

              {/* Numéro */}
              <text
                x={x}
                y={y + font / 3}
                fontSize={font}
                fill="#ffffff"
                textAnchor="middle"
                fontWeight={700}
              >
                {el.label ?? ""}
              </text>
            </g>
          );
        }


        if (el.kind === "text") {
          return (
            <text
              key={el.id}
              x={x}
              y={y}
              fontSize={14 * iconScale}
              fill={el.color || "#111827"}
              textAnchor="middle"
              dominantBaseline="middle"
            >
              {el.label || "Texte"}
            </text>
          );
        }

        if ((el.kind === "arrow" || el.kind === "pass") && el.x2 != null && el.y2 != null) {
          const x2 = el.x2 * width;
          const y2 = el.y2 * height;

          const drawHead = (fromX: number, fromY: number, toX: number, toY: number) => {
            const angle = Math.atan2(toY - fromY, toX - fromX);
            const head = 12 * iconScale;
            const hx1 = toX - head * Math.cos(angle - Math.PI / 6);
            const hy1 = toY - head * Math.sin(angle - Math.PI / 6);
            const hx2 = toX - head * Math.cos(angle + Math.PI / 6);
            const hy2 = toY - head * Math.sin(angle + Math.PI / 6);
            return `${toX},${toY} ${hx1},${hy1} ${hx2},${hy2}`;
          };

          if (el.kind === "pass") {
            return (
              <g key={el.id}>
                <line
                  x1={x}
                  y1={y}
                  x2={x2}
                  y2={y2}
                  stroke={el.color || "#111827"}
                  strokeWidth={3 * iconScale}
                  strokeDasharray="8 6"
                />
                <polygon points={drawHead(x, y, x2, y2)} fill={el.color || "#111827"} />
              </g>
            );
          }

          // arrow
          if (el.cpx != null && el.cpy != null) {
            const cx = el.cpx * width;
            const cy = el.cpy * height;
            // tangente au bout pour orienter la pointe
            const tx = 2 * (x2 - cx);
            const ty = 2 * (y2 - cy);
            const auxX = x2 - tx;
            const auxY = y2 - ty;

            return (
              <g key={el.id} fill={el.color || "#111827"} stroke={el.color || "#111827"}>
                <path d={`M ${x} ${y} Q ${cx} ${cy} ${x2} ${y2}`} fill="none" strokeWidth={3 * iconScale} />
                <polygon points={drawHead(auxX, auxY, x2, y2)} />
              </g>
            );
          }

          return (
            <g key={el.id} fill={el.color || "#111827"} stroke={el.color || "#111827"}>
              <line x1={x} y1={y} x2={x2} y2={y2} strokeWidth={3 * iconScale} />
              <polygon points={drawHead(x, y, x2, y2)} />
            </g>
          );
        }

        return null;
      })}
    </svg>
  );
};

/* ======================= NEW: Responsive helpers ======================= */
// Mesure la largeur du conteneur pour calculer H à ratio constant
// Composant qui rend un dessin en gardant le bon ratio, limité en largeur
const ResponsiveCourtTile: React.FC<{
  drawing: DrawingV2;
  t: (k: string) => string;
}> = ({ drawing, t }) => {
  const safe = {
    ...drawing,
    paths: sanitizePaths(drawing.paths),
    elements: Array.isArray(drawing.elements) ? drawing.elements : [],
  };

  const { ref, width } = useContainerWidth<HTMLDivElement>();

  // --- Réglages faciles à ajuster ---
  const MAX_W = 320;
  const HEIGHT_TWEAK = 1.3;
  const FULL_RATIO = 1;

  const W = Math.min(Math.max(0, width), MAX_W);
  const H =
    safe.type === "full"
      ? Math.round(W * FULL_RATIO)      // ex: 0.56 pour donner un peu plus de hauteur
      : Math.round(W * HEIGHT_TWEAK);   // half légèrement plus haut que 1:1

  return (
    <div className="pdf-avoid-break space-y-2">
      <div className="text-xs text-gray-500 dark:text-gray-400">
        {safe.type === "full" ? t("trainingdetails:court_full") : t("trainingdetails:court_half")}
      </div>

      {/* wrapper mesuré et limité en largeur */}
      <div ref={ref} className="relative w-full max-w-[520px] mx-auto">
        <div style={{ width: "100%", height: `${H}px`, position: "relative" }}>
          <div className="pdf-avoid-break">
            <TrainingDrawingCanvas
              onSave={() => { }}
              initialPaths={safe.paths}
              type={safe.type}
              readOnly
              targetWidth={W}
              targetHeightCss={H}
            />
          </div>
          <div className="absolute inset-0 pointer-events-none">
            <ElementsOverlayRO
              elements={safe.elements || []}
              width={W}
              height={H}
              iconScale={W / 520}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

/* ===================== END Responsive helpers ===================== */

// ---------- Composant principal ----------
export const TrainingDetails = () => {
  const { id } = useParams<{ id: string }>();
  const { t, lng } = useI18nSafe();
  const locale = lng === "fr" ? "fr-FR" : "en-US";
  const pdfRef = useRef<HTMLDivElement | null>(null);

  // --- state principal
  const [loading, setLoading] = useState(true);
  const [training, setTraining] = useState<Training | null>(null);
  const [exercises, setExercises] = useState<ExerciseBlock[] | null>(null);
  const [planId, setPlanId] = useState<string | null>(null);
  const [teamName, setTeamName] = useState<string | null>(null);

  // --- Edit date/time
  const [editOpen, setEditOpen] = useState(false);
  const [dateStr, setDateStr] = useState("");
  const [timeStr, setTimeStr] = useState("");
  const [savingDate, setSavingDate] = useState(false);

  // --- notes (nouveau)
  const [editNotesOpen, setEditNotesOpen] = useState(false);
  const [notesDraft, setNotesDraft] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);

  // états permissions
  const [userRole, setUserRole] = useState<"owner" | "coach" | "assistant" | "player" | "viewer" | null>(null);
  const [selfPlayerJsonId, setSelfPlayerJsonId] = useState<string | null>(null);
  const [canEdit, setCanEdit] = useState<boolean>(false);              // owner only
  const [canEditAttendance, setCanEditAttendance] = useState<boolean>(false); // staff + player (mais filtré)


  // --- attendance (base)
  const [teamPlayers, setTeamPlayers] = useState<TeamPlayer[]>([]);
  const [presentIds, setPresentIds] = useState<Set<string>>(new Set());
  const [showAttendanceModal, setShowAttendanceModal] = useState(false);
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [savingAttendance, setSavingAttendance] = useState(false);

  const [deletingPlan, setDeletingPlan] = useState(false);

  // --- multi-équipes
  const [allTeams, setAllTeams] = useState<TeamRow[]>([]);
  const [selectedExtraTeamIds, setSelectedExtraTeamIds] = useState<string[]>([]);
  const [sessionExtraTeamNames, setSessionExtraTeamNames] = useState<string[]>([]);
  const [teamSelectId, setTeamSelectId] = useState<string>("");
  const [modalPlayers, setModalPlayers] = useState<TeamPlayer[]>([]);

  const [deletingTraining, setDeletingTraining] = useState(false);

  // ⬇️ refs pour export PNG “cliché du bloc”
  const cardRefs = useRef<Record<string, HTMLDivElement | null>>({});
  // ⬇️ ouvre/ferme la modale de shooting
  const [showShootingModal, setShowShootingModal] = useState(false);
  const [shootingRefreshKey, setShootingRefreshKey] = useState(0);

  const isCapacitor = Capacitor.isNativePlatform();


  // ⬇️ transforme les présents en PlayerLite { firstName, lastName, number }
  const playersForShooting = useMemo(
    () =>
      teamPlayers
        .filter(
          (p) =>
            !!p.id &&
            presentIds.has(String(p.id)) &&
            typeof p.number === "number" &&
            Number.isFinite(p.number as number)
        )
        .map((p) => ({
          firstName: p.firstName || (p.name ? String(p.name).split(" ")[0] : ""),
          lastName:
            p.lastName || (p.name ? String(p.name).split(" ").slice(1).join(" ") : ""),
          number: p.number as number,
        })),
    [teamPlayers, presentIds]
  );


  const exportBlockPng = async (exId: string, name: string) => {
    const node = cardRefs.current[exId];
    if (!node) return;

    const canvas = await html2canvas(node, {
      backgroundColor: "#ffffff",
      scale: 2,
      useCORS: true,
      logging: false,
      onclone: (doc) => {
        doc.documentElement.classList.remove("dark");
        doc.body.classList.remove("dark");
        (doc.querySelectorAll(".dark") as unknown as HTMLElement[]).forEach((el) =>
          el.classList.remove("dark")
        );
        doc.documentElement.setAttribute("data-theme", "light");
        doc.body.setAttribute("data-theme", "light");
        const style = doc.createElement("style");
        style.textContent = `
          :root { color-scheme: light !important; }
          html, body { background: #fff !important; }
        `;
        doc.head.appendChild(style);
      },
    });

    const url = canvas.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(name || "bloc").replace(/[^\p{L}\p{N}_-]+/gu, "_").slice(0, 60)}.png`;
    a.click();
  };

  const handleDeleteTraining = async () => {
    if (!training) return;
    const ok = window.confirm(t("trainingdetails:confirm_delete_training"));
    if (!ok) return;

    setDeletingTraining(true);
    try {
      await supabase.from("training_attendance").delete().eq("training_id", training.id);
      await supabase.from("training_plans").delete().eq("training_id", training.id);
      const { error } = await supabase.from("trainings").delete().eq("id", training.id);
      if (error) {
        console.error(error);
        alert(t("trainingdetails:error_delete_training"));
        setDeletingTraining(false);
        return;
      }
      window.location.href = "/trainings";
    } finally {
      setDeletingTraining(false);
    }
  };

  const displayName = (p: TeamPlayer) =>
    p.name || [p.firstName, p.lastName].filter(Boolean).join(" ") || t("trainingdetails:unnamed");

  const uniqueById = (arr: TeamPlayer[]) => {
    const seen = new Set<string>();
    const out: TeamPlayer[] = [];
    for (const p of arr) {
      const pid = String(p.id || "");
      if (!pid) continue;
      if (!seen.has(pid)) {
        seen.add(pid);
        out.push(p);
      }
    }
    return out;
  };

  const fetchLatestPlan = async (trainingId: string) => {
    const { data, error } = await supabase
      .from("training_plans")
      .select("id, exercises, created_at")
      .eq("training_id", trainingId)
      .order("created_at", { ascending: false })
      .limit(1);

    if (error) {
      console.error("Erreur chargement training_plan:", error);
      setPlanId(null);
      setExercises(null);
      return;
    }
    if (data && data.length > 0) {
      setPlanId(data[0].id);
      setExercises(data[0].exercises as ExerciseBlock[]);
    } else {
      setPlanId(null);
      setExercises(null);
    }
  };

  const attendees = useMemo(
    () => teamPlayers.filter((p) => p.id && presentIds.has(p.id)),
    [teamPlayers, presentIds]
  );
  const missingIdCount = useMemo(() => teamPlayers.filter((p) => !p.id).length, [teamPlayers]);

  // --- load training + plans + teamPlayers + attendance + allTeams
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      if (!id) return;
      setLoading(true);

      try {
        const { data: trainingData, error: tErr } = await supabase
          .from("trainings")
          .select("*")
          .eq("id", id)
          .maybeSingle();

        if (tErr || !trainingData) {
          console.error("Erreur chargement training:", tErr);
          return;
        }

        const trow = trainingData as Training;
        if (cancelled) return;
        setTraining(trow);

        // qui est connecté ?
        const { data: auth } = await supabase.auth.getUser();
        const uid = auth?.user?.id || null;

        // calcul des droits par rôle
        if (trow.team_id && uid) {
          const { data: tmRow, error: tmErr } = await supabase
            .from("team_members")
            .select("role, player_json_id")
            .eq("team_id", trow.team_id)
            .eq("user_id", uid)
            .maybeSingle();

          const role = (tmRow?.role as any) ?? null;
          setUserRole(role);
          setSelfPlayerJsonId(tmRow?.player_json_id ?? null);

          // 👉 Règle demandée : cacher les boutons SEULEMENT pour le rôle "player"
          if (role === "player") {
            setCanEdit(false);             // pas d’édition (date/notes/plan/boutons)
            setCanEditAttendance(true);    // peut gérer SA présence (déjà géré plus bas)
          } else {
            setCanEdit(true);              // coach/assistant/viewer → édition autorisée
            setCanEditAttendance(true);    // staff & même viewer : on laisse la modale (si tu veux, passe à false)
          }

          if (tmErr) {
            console.warn("team_members lookup error:", tmErr);
          }
        } else {
          // ❗️ Pas de ligne team_members (ex: owner non enregistré) → on autorise l’édition
          setUserRole(null);
          setSelfPlayerJsonId(null);
          setCanEdit(true);
          setCanEditAttendance(true);
        }


        // init champs date/heure pour l'édition
        const [d0, h0] = splitIsoToLocalDateTime(trow.date);
        setDateStr(d0);
        setTimeStr(h0);

        const planPromise = fetchLatestPlan(id);

        let players: TeamPlayer[] = [];
        let teamNameLocal: string | null = null;

        const teamPromise = (async () => {
          if (trow.team_id) {
            const { data: teamRow, error: teamErr } = await supabase
              .from("teams")
              .select("players, team_name")
              .eq("id", trow.team_id)
              .maybeSingle();

            if (teamErr) {
              console.error("Erreur chargement équipe:", teamErr);
            } else if (teamRow) {
              if (teamRow.players) {
                players = (teamRow.players as TeamPlayer[]).filter((p) => !!p?.id);
              }
              teamNameLocal = teamRow.team_name ?? null;
            }
          }
        })();

        const allTeamsPromise = supabase.from("teams").select("id, team_name, players");

        const attendancePromise = supabase
          .from("training_attendance")
          .select("player_id, was_present")
          .eq("training_id", id);

        const [attRes, allTeamsRes] = await Promise.all([attendancePromise, allTeamsPromise, planPromise, teamPromise]);
        if (cancelled) return;

        const { data: attRows, error: attErr } = attRes as {
          data: { player_id: string; was_present: boolean }[] | null;
          error: any;
        };
        if (attErr) {
          console.error("Erreur chargement attendance:", attErr);
        } else {
          const ids = new Set((attRows || []).filter((r) => r.was_present).map((r) => String(r.player_id)));
          setPresentIds(ids);
        }

        const { data: allTeamsData, error: allTErr } = allTeamsRes as {
          data: TeamRow[] | null;
          error: any;
        };
        if (allTErr) {
          console.error("Erreur chargement toutes équipes:", allTErr);
        } else if (allTeamsData) {
          setAllTeams(
            allTeamsData.map((t) => ({ id: t.id, team_name: t.team_name, players: (t.players as any) ?? [] }))
          );
        }

        setTeamPlayers(players);
        setTeamName(teamNameLocal);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const handleDeletePlan = async () => {
    if (!planId || !training) return;
    const ok = window.confirm(t("trainingdetails:confirm_delete_plan"));
    if (!ok) return;

    setDeletingPlan(true);
    try {
      const { error } = await supabase.from("training_plans").delete().eq("id", planId);
      if (error) {
        console.error("Erreur suppression plan:", error);
        alert(t("trainingdetails:error_delete_plan"));
        return;
      }
      await fetchLatestPlan(training.id);
    } finally {
      setDeletingPlan(false);
    }
  };

  // 🔎 helpers pour nom + type
  const inferKind = (ex: ExerciseBlock, idx: number): BlockKind => {
    if (ex.kind) return ex.kind;
    const raw = (ex.title || "").toLowerCase();
    if (raw.includes("échauf") || raw.includes("echauf") || raw.includes("warm")) return "echauffement";
    if (raw.includes("prépa") || raw.includes("prepa") || raw.includes("phys")) return "prepa";
    if (idx === 0) return "echauffement";
    return "exercice";
  };
  const kindLabel = (k: BlockKind) =>
    k === "echauffement"
      ? t("trainingdetails:kind.warmup")
      : k === "prepa"
        ? t("trainingdetails:kind.conditioning")
        : k === "shooting"
          ? (t("trainingdetails:kind.shooting") || "Shooting")
          : t("trainingdetails:kind.drill");

  const kindPillClass = (k: BlockKind) =>
    k === "echauffement"
      ? "bg-orange-100 text-orange-800 border-orange-200"
      : k === "prepa"
        ? "bg-green-100 text-green-800 border-green-200"
        : k === "shooting"
          ? "bg-purple-100 text-purple-800 border-purple-200"
          : "bg-blue-100 text-blue-800 border-blue-200";

  // --- Date + Heure pour l’affichage
  const iso = training?.date || "";
  const dt = iso ? new Date(iso) : null;
  const dateLabel = dt
    ? dt.toLocaleDateString(locale, {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    })
    : "";
  const timeLabel = /T\d{2}:\d{2}/.test(iso)
    ? dt?.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" })
    : null;

  const headerTeams = [teamName || "—", ...sessionExtraTeamNames].filter(Boolean).join(" + ");

  // ----- sauvegarde date/heure
  const handleOpenEdit = () => {
    const [d0, h0] = splitIsoToLocalDateTime(training?.date);
    setDateStr(d0);
    setTimeStr(h0);
    setEditOpen(true);
  };

  const handleSaveDateTime = async () => {
    if (!training) return;
    if (!dateStr) {
      alert(t("trainingdetails:date_required"));
      return;
    }
    setSavingDate(true);
    try {
      const newValue = combineLocalToStored(dateStr, timeStr);
      const { error } = await supabase.from("trainings").update({ date: newValue }).eq("id", training.id);

      if (error) {
        console.error(error);
        alert(t("trainingdetails:error_save_datetime"));
        return;
      }

      setTraining({ ...training, date: newValue });
      setEditOpen(false);
    } finally {
      setSavingDate(false);
    }
  };

  // ----- notes: open modal + save
  const handleOpenEditNotes = () => {
    setNotesDraft(training?.notes || "");
    setEditNotesOpen(true);
  };

  const handleSaveNotes = async () => {
    if (!training) return;
    setSavingNotes(true);
    try {
      const { error } = await supabase.from("trainings").update({ notes: notesDraft }).eq("id", training.id);
      if (error) {
        console.error(error);
        alert(t("trainingdetails:error_save_notes"));
        return;
      }
      setTraining({ ...training, notes: notesDraft });
      setEditNotesOpen(false);
    } finally {
      setSavingNotes(false);
    }
  };

  if (loading) {
    return <LogoLoader />;
  }

  if (!training) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 text-gray-900 dark:text-gray-100">
        <div className="max-w-4xl mx-auto p-6">
          <div className="mb-4">
            <Link
              to="/trainings"
              className="text-blue-600 dark:text-blue-400 hover:underline inline-flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" /> {t("trainingdetails:back")}
            </Link>
          </div>
          <p className="text-red-600 dark:text-red-400">{t("trainingdetails:not_found")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 text-gray-900 dark:text-gray-100 transition-colors">
      <div className="max-w-5xl mx-auto p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Link
              to="/trainings"
              className="px-3 py-2 rounded border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 inline-flex items-center gap-2 text-sm
               focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
            >
              <ArrowLeft className="w-4 h-4" /> {t("trainingdetails:back")}
            </Link>
            {canEdit && training?.id && training?.date && new Date(training.date) >= new Date() && (
              <SendReminderButton type="training" eventId={training.id} />
            )}
            {canEdit && (
              <button
                onClick={handleDeleteTraining}
                disabled={deletingTraining}
                className="px-3 py-2 rounded border border-red-300 dark:border-red-700 text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/30 inline-flex items-center gap-2 text-sm disabled:opacity-60
               focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400"
              >
                <Trash2 className="w-4 h-4" />
                {deletingTraining ? t("trainingdetails:deleting") : t("trainingdetails:delete")}
              </button>
            )}
          </div>
        </div>

        {/* Infos principales */}
        <div className="bg-white dark:bg-gray-900 rounded border border-gray-200 dark:border-gray-800 shadow-sm p-4 mb-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-3 text-gray-700 dark:text-gray-300">
              <div className="flex items-center gap-2">
                <CalendarDays className="w-4 h-4" />
                <span className="font-semibold">{t("trainingdetails:date")} :</span>
                <span>{dateLabel || "—"}</span>
              </div>
              {timeLabel && (
                <div className="inline-flex items-center gap-1 text-gray-600 dark:text-gray-400">
                  <Clock className="w-4 h-4" />
                  {timeLabel}
                </div>
              )}
            </div>
            {canEdit && (
              <button
                onClick={handleOpenEdit}
                className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800
                         focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
              >
                <Pencil className="w-4 h-4" />
                {t("trainingdetails:edit_datetime")}
              </button>
            )}
          </div>

          <div className="mt-3 flex items-center gap-2 text-gray-700 dark:text-gray-300">
            <Users className="w-4 h-4" />
            <span className="font-semibold">{t("trainingdetails:team")} :</span>
            <span>{headerTeams || "—"}</span>
          </div>

          {/* Notes */}
          <div className="mt-4">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2 font-semibold">
                <NotebookPen className="w-4 h-4" />
                {t("trainingdetails:notes")}
              </div>
              {canEdit && (
                <button
                  onClick={handleOpenEditNotes}
                  className="inline-flex items-center gap-2 text-xs px-2 py-1 rounded border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  <Pencil className="w-4 h-4" />
                  {training.notes ? t("trainingdetails:edit_note") : t("trainingdetails:add_note")}
                </button>
              )}
            </div>
            {training.notes ? (
              <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{training.notes}</p>
            ) : (
              <p className="text-gray-500 dark:text-gray-400 text-sm">
                {t("trainingdetails:no_note")}
              </p>
            )}
          </div>
        </div>

        {/* Présences */}
        <div className="bg-white dark:bg-gray-900 rounded border border-gray-200 dark:border-gray-800 shadow-sm p-4 mb-6">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 font-semibold">
              <Users className="w-4 h-4" />
              {t("trainingdetails:attendees")}
            </div>
            <button
              onClick={() => {
                rebuildModalPlayers();
                setShowAttendanceModal(true);
              }}
              className="inline-flex items-center gap-2 text-sm px-2 py-1 rounded border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800
                         focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
              title={t("trainingdetails:edit_attendance")}
            >
              <Pencil className="w-4 h-4" />
              {t("trainingdetails:edit")}
            </button>
          </div>

          {missingIdCount > 0 && (
            <div className="mb-2 text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-600 rounded p-2">
              {t("trainingdetails:missing_ids", { count: missingIdCount })}
            </div>
          )}

          {attendees.length > 0 ? (
            <ul className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
              {attendees.map((p, idx) => (
                <li key={p.id || idx} className="border border-gray-200 dark:border-gray-800 rounded p-2 text-sm">
                  <div className="flex flex-col">
                    <span className="font-semibold">
                      {p.number != null ? `#${p.number} ` : ""}
                      {displayName(p)}
                    </span>
                    {p.position && <span className="text-gray-500 dark:text-gray-400">{p.position}</span>}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-gray-600 dark:text-gray-400">{t("trainingdetails:no_attendees")}</p>
          )}
        </div>

        {/* Plan d’entraînement */}
        <div ref={pdfRef} className="bg-white dark:bg-gray-900 rounded border border-gray-200 dark:border-gray-800 shadow-sm p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 font-semibold">
              <NotebookPen className="w-4 h-4" />
              {t("trainingdetails:plan_title")}
            </div>
            <div className="flex items-center gap-2">
              {!isCapacitor && (
                <ExportTrainingPlanPdfButton
                  targetRef={pdfRef as any}
                  filename={`seance_${training.id}_${dateStr || "date"}`}
                  label={t("trainingdetails:export_pdf") || "Exporter PDF"}
                />
              )}
              {canEdit && (
                planId ? (
                  <div className="flex flex-wrap items-center gap-2 sm:gap-3 justify-end">
                    <Link
                      to={`/plantraining?training_id=${training.id}`}
                      className="inline-flex items-center justify-center gap-2 rounded border text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-900
       hover:bg-blue-50 dark:hover:bg-blue-900/30
       h-11 w-11 sm:h-auto sm:w-auto sm:px-3 sm:py-2 text-sm
       focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
                      aria-label={t("trainingdetails:edit_plan")}
                      title={t("trainingdetails:edit_plan")}
                    >
                      <Pencil className="w-5 h-5 sm:w-4 sm:h-4" />
                      <span className="hidden sm:inline whitespace-nowrap">{t("trainingdetails:edit")}</span>
                    </Link>

                    <button
                      onClick={handleDeletePlan}
                      disabled={deletingPlan}
                      className="inline-flex items-center justify-center gap-2 rounded border text-red-700 dark:text-red-300 border-red-200 dark:border-red-900
       hover:bg-red-50 dark:hover:bg-red-900/30
       h-11 w-11 sm:h-auto sm:w-auto sm:px-3 sm:py-2 text-sm disabled:opacity-60
       focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-400"
                      aria-label={t("trainingdetails:delete_plan")}
                      title={t("trainingdetails:delete_plan")}
                    >
                      <Trash2 className="w-5 h-5 sm:w-4 sm:h-4" />
                      <span className="hidden sm:inline whitespace-nowrap">
                        {deletingPlan ? t("trainingdetails:deleting") : t("trainingdetails:delete")}
                      </span>
                    </button>
                  </div>
                ) : (
                  <Link
                    to={`/plantraining?training_id=${training.id}`}
                    className="inline-flex items-center justify-center gap-2 rounded border text-green-700 dark:text-green-300 border-green-200 dark:border-green-900
     hover:bg-green-50 dark:hover:bg-green-900/30
     h-11 w-full sm:w-auto sm:px-3 sm:py-2 text-sm
     focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-400"
                    aria-label={t("trainingdetails:create_plan")}
                    title={t("trainingdetails:create_plan")}
                  >
                    <PlusCircle className="w-5 h-5 sm:w-4 sm:h-4" />
                    <span className="hidden sm:inline whitespace-nowrap">{t("trainingdetails:create_plan_btn")}</span>
                    <span className="sm:hidden">{t("trainingdetails:plan_short")}</span>
                  </Link>
                )
              )}

            </div>
          </div>

          {!exercises || exercises.length === 0 ? (
            <p className="text-gray-600 dark:text-gray-400">
              {t("trainingdetails:no_plan")}
            </p>
          ) : (
            <div className="space-y-6">
              {exercises.map((ex, idx) => {
                const k = inferKind(ex, idx);
                const isShooting = (ex.kind === "shooting");
                const title =
                  (ex.name && ex.name.trim()) ||
                  ex.title ||
                  (isShooting ? (t("trainingdetails:kind.shooting") || "Shooting") : `${t("trainingdetails:block")} ${idx + 1}`);

                return (
                  <div
                    key={ex.id}
                    ref={(el) => {
                      cardRefs.current[ex.id] = el;
                    }}
                    className="border border-gray-200 dark:border-gray-800 rounded p-4"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-lg">{title}</h3>
                        <span className={`px-2 py-0.5 text-xs font-semibold rounded-full border ${kindPillClass(isShooting ? "shooting" : k)}`}>
                          {kindLabel(isShooting ? "shooting" : k)}
                        </span>
                      </div>

                      <div className="flex items-center gap-2">
                        {!isShooting && (
                          <div className="text-sm text-gray-600 dark:text-gray-400">
                            {t("trainingdetails:duration")} : <span className="font-medium">{ex.duration || "—"}</span>
                          </div>
                        )}
                        {isShooting && (
                          <button
                            onClick={() => setShowShootingModal(true)}
                            className="inline-flex items-center gap-2 text-xs px-2 py-1 rounded border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800"
                            title={t("trainingdetails:edit_shooting") || "Modifier les tirs"}
                          >
                            <Pencil className="w-4 h-4" />
                            {t("trainingdetails:edit_shooting") || "Modifier"}
                          </button>
                        )}

                        {/* Export PNG du bloc */}
                        {canEdit && !isCapacitor && (
                          <button
                            onClick={() => exportBlockPng(ex.id, title)}
                            className="inline-flex items-center gap-2 text-xs px-2 py-1 rounded border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800"
                            title={t("trainingdetails:export_png")}
                          >
                            PNG
                          </button>
                        )}
                      </div>
                    </div>

                    {isShooting ? (
                      <div className="-mt-1">
                        <ShootingBlockViewer key={shootingRefreshKey} trainingId={training.id} />
                      </div>
                    ) : (
                      <>
                        {(ex.objectives || []).some((o) => (o || "").trim() !== "") && (
                          <div className="pdf-avoid-break mb-3">
                            <div className="text-sm font-semibold mb-1">{t("trainingdetails:objectives")}</div>
                            <ul className="list-disc ml-5 text-sm text-gray-700 dark:text-gray-300">
                              {(ex.objectives || [])
                                .filter((o) => (o || "").trim() !== "")
                                .map((o, i) => (
                                  <li key={i}>{o}</li>
                                ))}
                            </ul>
                          </div>
                        )}

                        {ex.description && (
                          <div className="pdf-avoid-break mb-3">
                            <div className="text-sm font-semibold mb-1">{t("trainingdetails:flow")}</div>
                            <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                              {ex.description}
                            </p>
                          </div>
                        )}

                        {ex.technicalNotes && (
                          <div className="pdf-avoid-break mb-3">
                            <div className="text-sm font-semibold mb-1">{t("trainingdetails:tech_notes")}</div>
                            <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                              {ex.technicalNotes}
                            </p>
                          </div>
                        )}

                        {ex.evolutions && (
                          <div className="pdf-avoid-break mb-3">
                            <div className="text-sm font-semibold mb-1">{t("trainingdetails:evolutions")}</div>
                            <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                              {ex.evolutions}
                            </p>
                          </div>
                        )}

                        {ex.drawings && ex.drawings.length > 0 && (
                          <div className="grid gap-4 sm:grid-cols-2">
                            {ex.drawings.map((d) => (
                              <div key={d.id} className="pdf-avoid-break">
                                <ResponsiveCourtTile drawing={d} t={t} />
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ⛔️ on n’affiche plus le viewer global sous le plan */}
        {/* <ShootingBlockViewer trainingId={training.id} /> */}

        {/* --- MODAL EDIT DATE/HEURE --- */}
        {editOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/40" onClick={() => setEditOpen(false)} />
            <div className="relative z-10 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-xl w-full max-w-md p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold">{t("trainingdetails:edit_datetime")}</h3>
                {canEdit && (
                  <button
                    onClick={() => setEditOpen(false)}
                    className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800"
                    aria-label={t("trainingdetails:close")}
                  >
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>

              <div className="space-y-3">
                <div>
                  <label className="block text-sm mb-1">{t("trainingdetails:date")}</label>
                  <input
                    type="date"
                    value={dateStr}
                    onChange={(e) => setDateStr(e.target.value)}
                    className="w-full rounded border px-3 py-2 bg-white border-gray-300 text-gray-900
                               focus:outline-none focus:ring-2 focus:ring-blue-400
                               dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100"
                  />
                </div>

                <div>
                  <label className="block text-sm mb-1">{t("trainingdetails:time_optional")}</label>
                  <input
                    type="time"
                    value={timeStr}
                    onChange={(e) => setTimeStr(e.target.value)}
                    className="w-full rounded border px-3 py-2 bg-white border-gray-300 text-gray-900
                               focus:outline-none focus:ring-2 focus:ring-blue-400
                               dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {t("trainingdetails:all_day_hint")}
                  </p>
                </div>
              </div>

              <div className="mt-4 flex justify-end gap-2">
                {canEdit && (
                  <button
                    onClick={() => setEditOpen(false)}
                    className="px-3 py-2 rounded border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 text-sm">
                    {t("trainingdetails:cancel")}
                  </button>
                )}
                {canEdit && (
                  <button
                    onClick={handleSaveDateTime}
                    disabled={savingDate}
                    className="px-3 py-2 rounded bg-blue-600 hover:bg-blue-700 text-white text-sm inline-flex items-center gap-2 disabled:opacity-60"
                  >
                    <Save className="w-4 h-4" />
                    {savingDate ? t("trainingdetails:saving") : t("trainingdetails:save")}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* --- MODAL EDIT NOTES --- */}
        {editNotesOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/40" onClick={() => setEditNotesOpen(false)} />
            <div className="relative z-10 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-xl w-full max-w-lg p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold">{t("trainingdetails:edit_note_title")}</h3>
                {canEdit && (
                  <button
                    onClick={() => setEditNotesOpen(false)}
                    className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800"
                    aria-label={t("trainingdetails:close")}
                  >
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>

              <div>
                <label className="block text-sm mb-1">{t("trainingdetails:notes")}</label>
                <textarea
                  value={notesDraft}
                  onChange={(e) => setNotesDraft(e.target.value)}
                  rows={8}
                  className="w-full rounded border px-3 py-2 bg-white border-gray-300 text-gray-900
                             focus:outline-none focus:ring-2 focus:ring-blue-400
                             dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100"
                  placeholder={t("trainingdetails:notes_placeholder")}
                />
              </div>

              <div className="mt-4 flex justify-end gap-2">
                {canEdit && (
                  <button
                    onClick={() => setEditNotesOpen(false)}
                    className="px-3 py-2 rounded border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 text-sm">
                    {t("trainingdetails:cancel")}
                  </button>
                )}
                {canEdit && (
                  <button
                    onClick={handleSaveNotes}
                    disabled={savingNotes}
                    className="px-3 py-2 rounded bg-blue-600 hover:bg-blue-700 text-white text-sm inline-flex items-center gap-2 disabled:opacity-60"
                  >
                    <Save className="w-4 h-4" />
                    {savingNotes ? t("trainingdetails:saving") : t("trainingdetails:save")}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* --- MODAL EDIT ATTENDANCE --- */}
        {showAttendanceModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div
              className="absolute inset-0 bg-black/40"
              onClick={() => setShowAttendanceModal(false)}
              aria-hidden="true"
            />
            <div className="relative z-10 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow-xl w-full max-w-2xl p-4 text-gray-900 dark:text-gray-100">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold">{t("trainingdetails:edit_attendance")}</h3>
                {canEditAttendance && (
                  <button
                    onClick={() => {
                      rebuildModalPlayers();
                      setShowAttendanceModal(true);
                    }}
                    className="inline-flex items-center gap-2 text-sm px-2 py-1 rounded border ..."
                    title={t("trainingdetails:edit_attendance")}
                  >
                    <Pencil className="w-4 h-4" />
                    {t("trainingdetails:edit")}
                  </button>
                )}
              </div>

              {/* Ajout d'équipe */}
              {userRole !== "player" && (
                <div className="mb-3 p-3 rounded border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/40">
                  <div className="text-sm font-semibold mb-2 flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    {t("trainingdetails:add_team")}
                  </div>

                  <div className="flex flex-col sm:flex-row gap-2">
                    <select
                      className="flex-1 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 p-2 text-sm"
                      value={teamSelectId}
                      onChange={(e) => setTeamSelectId(e.target.value)}
                    >
                      <option value="">{t("trainingdetails:select_team")}</option>
                      {allTeams
                        .filter((t) => t.id !== (training?.team_id || ""))
                        .map((t) => (
                          <option key={t.id} value={t.id}>
                            {t.team_name}
                          </option>
                        ))}
                    </select>
                    <button
                      type="button"
                      onClick={addExtraTeam}
                      className="inline-flex items-center gap-2 px-3 py-2 rounded bg-green-600 hover:bg-green-700 text-white text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-400"
                    >
                      <PlusCircle className="w-4 h-4" />
                      {t("trainingdetails:add_team_btn")}
                    </button>
                  </div>

                  <p className="mt-2 text-xs text-gray-600 dark:text-gray-400">
                    {t("trainingdetails:add_team_hint")}
                  </p>
                </div>
              )}

              <div className="max-h-80 overflow-auto border border-gray-200 dark:border-gray-800 rounded">
                {modalPlayers.length === 0 ? (
                  <div className="p-3 text-gray-600 dark:text-gray-400 text-sm">
                    {teamPlayers.length === 0
                      ? t("trainingdetails:no_players_hint")
                      : t("trainingdetails:no_players")}
                  </div>
                ) : (
                  <ul>
                    {modalPlayers.map((p) => {
                      if (!p.id) return null;
                      const label = `${p.number != null ? `#${p.number} ` : ""}${displayName(p)}`;
                      return (
                        <li key={p.id} className="px-3 py-2 border-b border-gray-200 dark:border-gray-800 flex items-center gap-2">
                          <input
                            id={`chk-${p.id}`}
                            type="checkbox"
                            className="h-4 w-4 accent-orange-600 dark:accent-orange-400"
                            checked={!!checked[p.id]}
                            onChange={() => toggleOne(p.id!)}
                          />
                          <label htmlFor={`chk-${p.id}`} className="text-sm cursor-pointer select-none">
                            {label}
                            {p.position ? <span className="text-gray-500 dark:text-gray-400"> — {p.position}</span> : null}
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>

              <div className="mt-4 flex justify-end gap-2">
                <button
                  onClick={() => setShowAttendanceModal(false)}
                  className="px-3 py-2 rounded border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 text-sm
                     focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400"
                >
                  {t("trainingdetails:cancel")}
                </button>
                <button
                  onClick={saveAttendance}
                  disabled={savingAttendance}
                  className="px-3 py-2 rounded bg-blue-600 hover:bg-blue-700 text-white text-sm disabled:opacity-60
                     focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
                >
                  {savingAttendance ? t("trainingdetails:saving") : t("trainingdetails:save")}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* --- MODALE SHOOTING (séparée, pas imbriquée) --- */}
        {showShootingModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div
              className="absolute inset-0 bg-black/40"
              onClick={() => setShowShootingModal(false)}
              aria-hidden="true"
            />
            <div className="relative z-10 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl shadow-xl w-[95vw] max-w-5xl max-h-[88vh] overflow-hidden">
              <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-200 dark:border-gray-800">
                <Pencil className="w-4 h-4" />
                <h3 className="font-semibold">
                  {t("trainingdetails:edit_shooting") || "Modifier le bloc de tirs"}
                </h3>
                <button
                  onClick={() => setShowShootingModal(false)}
                  className="ml-auto p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800"
                  aria-label={t("trainingdetails:close")}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-4 overflow-auto" style={{ maxHeight: "78vh" }}>
                <ShootingBlock
                  training={{ id: training.id }}
                  players={playersForShooting}
                  defaultTitle={t("trainingdetails:kind.shooting") || "Shooting"}
                  onSaved={() => {
                    setShootingRefreshKey((k) => k + 1); // rafraîchir le viewer
                    // setShowShootingModal(false); // décommente pour fermer auto
                  }}
                />
                {playersForShooting.length === 0 && (
                  <p className="mt-3 text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-600 rounded p-2">
                    {t("trainingdetails:no_number_hint") ||
                      "Aucun joueur présent avec un numéro — le suivi des tirs nécessite des numéros."}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  function rebuildModalPlayers() {
    // base = joueurs de l'équipe principale de la séance
    const base = teamPlayers.filter((p) => !!p?.id);
    const extras: TeamPlayer[] = [];

    // staff peut ajouter des équipes; player non
    if (userRole !== "player") {
      for (const tid of selectedExtraTeamIds) {
        const t = allTeams.find((tt) => tt.id === tid);
        if (t?.players?.length) {
          extras.push(...t.players.filter((p) => !!p?.id));
        }
      }
    }

    let merged = uniqueById([...base, ...extras]);

    // si joueur : ne garder que lui-même
    if (userRole === "player" && selfPlayerJsonId) {
      merged = merged.filter((p) => String(p.id) === String(selfPlayerJsonId));
    }

    setModalPlayers(merged);

    const next: Record<string, boolean> = {};
    merged.forEach((p) => {
      const pid = String(p.id);
      if (pid) next[pid] = presentIds.has(pid);
    });
    setChecked(next);
  }


  function addExtraTeam() {
    if (!teamSelectId) return;
    if (selectedExtraTeamIds.includes(teamSelectId)) return;
    if (teamSelectId === (training?.team_id || "")) return;
    const next = [...selectedExtraTeamIds, teamSelectId];
    setSelectedExtraTeamIds(next);
    setTimeout(rebuildModalPlayers, 0);
  }

  async function saveAttendance() {
    if (!training) return;
    setSavingAttendance(true);
    try {
      // Cas joueur : ne peut toucher qu'à SA ligne
      if (userRole === "player" && selfPlayerJsonId) {
        const pid = String(selfPlayerJsonId);
        const wantPresent = !!checked[pid];

        // supprimer uniquement sa ligne existante pour cette séance
        await supabase
          .from("training_attendance")
          .delete()
          .eq("training_id", training.id)
          .eq("player_id", pid);

        if (wantPresent) {
          const { error: insErr } = await supabase.from("training_attendance").insert({
            training_id: training.id,
            player_id: pid,
            was_present: true,
          });
          if (insErr) console.error("Erreur insert attendance (player):", insErr);
        }

        // mettre à jour l'écran
        const next = new Set(presentIds);
        if (wantPresent) next.add(pid); else next.delete(pid);
        setPresentIds(next);

        setShowAttendanceModal(false);
        return;
      }

      // Cas staff : peut gérer toutes les présences visibles
      await supabase.from("training_attendance").delete().eq("training_id", training.id);

      const rowsToInsert = modalPlayers
        .filter((p) => p.id && checked[p.id])
        .map((p) => ({ training_id: training.id, player_id: p.id as string, was_present: true }));

      if (rowsToInsert.length > 0) {
        const { error: insErr } = await supabase.from("training_attendance").insert(rowsToInsert);
        if (insErr) console.error("Erreur insert attendance (staff):", insErr);
      }

      const newPresent = new Set(rowsToInsert.map((r) => r.player_id));
      setPresentIds(newPresent);

      // mettre à jour les noms d'équipes affichés si staff a ajouté des équipes
      const mergedForPage = uniqueById([...teamPlayers, ...modalPlayers]);
      setTeamPlayers(mergedForPage);

      const extraNames = selectedExtraTeamIds
        .map((tid) => allTeams.find((t) => t.id === tid)?.team_name)
        .filter((n): n is string => !!n);
      setSessionExtraTeamNames(extraNames);

      setShowAttendanceModal(false);
    } finally {
      setSavingAttendance(false);
    }
  }


  function toggleOne(pid: string) {
    setChecked((prev) => ({ ...prev, [pid]: !prev[pid] }));
  }
};

export default TrainingDetails;
